import { createSlice } from '@reduxjs/toolkit'
import { format } from 'date-fns'

import i18n from '@/config/i18n'
import history from '@/config/router/history'
import { startAppListening } from '@/config/store/listenerMiddleware'
import { startInitialization } from '@/ducks/app/slices/app.shared'
import { authApi } from '@/ducks/auth/api/auth.api'
import {
  accessForbidden,
  fetchMyRoles,
  sessionExpired
} from '@/ducks/auth/auth.shared'
import { Permission, StoreLoginAs } from '@/ducks/auth/auth.types'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import type { RootState } from '@/ducks/root.reducer'
import {
  readFromLocalStorage,
  removeFromLocalStorage,
  setToLocalStorage
} from '@/services/localStorage'
import { setClarityPostLogin } from '@/utils/clarity.utils'
import { logFirebaseEvent } from '@/utils/firebase.utils'

const LOGIN_PATH = '/login'
const ORDERS_PATH = '/orders'

export interface AuthState {
  token?: string | null
  authSource?: string | null
  login_as?: StoreLoginAs | null
  permissions: Permission[]
  rolesLoaded: boolean
  /** A /me/roles request is in flight — used to skip duplicate fetches. */
  rolesPending: boolean
  rolesError: boolean
}

export const initialState = {
  token: undefined,
  authSource: undefined,
  login_as: undefined as StoreLoginAs | undefined,
  permissions: [],
  rolesLoaded: false,
  rolesPending: false,
  rolesError: false
}

const initializeState = (): AuthState => ({
  token: readFromLocalStorage('token') || undefined,
  authSource: readFromLocalStorage('authSource') || undefined,
  login_as: undefined,
  permissions: [],
  rolesLoaded: false,
  rolesPending: false,
  rolesError: false
})

const authSlice = createSlice({
  name: 'auth',
  initialState: initializeState,
  reducers: {
    /** Terminal fallback so a gated route can never wait on nothing. */
    rolesFetchFailed: state => {
      state.rolesPending = false
      state.rolesError = true
    },
    logout: () => {
      return { ...initialState }
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      authApi.endpoints.login.matchFulfilled,
      (state, { payload }) => {
        state.token = payload.token
        state.authSource = payload.authSource || null
        state.login_as = null
        state.permissions = []
        state.rolesLoaded = false
        state.rolesPending = false
        state.rolesError = false
      }
    )
    builder.addMatcher(authApi.endpoints.getMyRoles.matchPending, state => {
      state.rolesPending = true
      state.rolesError = false
    })
    builder.addMatcher(
      authApi.endpoints.getMyRoles.matchFulfilled,
      (state, { payload }) => {
        state.permissions = payload.permissions
        state.rolesLoaded = true
        state.rolesPending = false
        state.rolesError = false
        if (payload.login_as === 'owner' || payload.login_as === 'store_user') {
          state.login_as = payload.login_as
        }
      }
    )
    // Not "loaded with no permissions": treating a failed fetch as an empty
    // permission set silently demoted owners to staff defaults and redirected
    // them off /billing and /performance. Gated routes surface the error.
    builder.addMatcher(
      authApi.endpoints.getMyRoles.matchRejected,
      (state, action) => {
        // Checked before touching rolesPending: a request skipped by RTK
        // Query's condition means another one is still in flight and owns the
        // outcome. Clearing the flag here would re-open the de-dup guard and
        // let the predicate listener fire another fetch.
        if (action.meta?.condition) return
        state.rolesPending = false
        state.rolesError = true
      }
    )
  }
})

startAppListening({
  actionCreator: authSlice.actions.logout,
  effect: async function syncStorage(_, listenerApi) {
    listenerApi.dispatch(authApi.util.resetApiState())
    removeFromLocalStorage('token', 'authSource')

    history.push(LOGIN_PATH)
  }
})

/**
 * RBAC turns on from a LaunchDarkly flag that can arrive after boot. Roles used
 * to be fetched only inside the one-shot boot chain, so a late flag left
 * `rolesLoaded` false forever and gated pages rendered nothing.
 */
const isRbacEnabled = (state: RootState): boolean => {
  const flags = state.app?.flags

  return Boolean(
    flags?.['store-auth-v4-use-lmd-core'] ||
      flags?.store_auth_v4_use_lmd_core ||
      state.auth?.authSource === 'lmd-core'
  )
}

const shouldFetchRoles = (state: RootState): boolean => {
  const auth = state.auth

  return Boolean(
    auth?.token &&
      isRbacEnabled(state) &&
      !auth.rolesLoaded &&
      !auth.rolesPending &&
      !auth.rolesError
  )
}

startAppListening({
  predicate: (_, currentState, previousState) =>
    shouldFetchRoles(currentState) && !shouldFetchRoles(previousState),
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(fetchMyRoles())
  }
})

startAppListening({
  actionCreator: fetchMyRoles,
  effect: async (_, listenerApi) => {
    const state = listenerApi.getState()
    const token = state.auth?.token
    // /me/roles only exists for lmd-core auth: never call it otherwise, or a
    // legacy store would take a 401 here and be signed out.
    if (!token || !isRbacEnabled(state)) return

    // Login and the boot chain both ask for roles. Skipping a duplicate is
    // right; cancelling the in-flight one was not — the replacement could be
    // dropped by RTK Query's in-flight condition, leaving the permission set
    // neither loaded nor failed, which hangs every gated route on a spinner.
    if (state.auth?.rolesPending) return

    try {
      await listenerApi
        .dispatch(
          authApi.endpoints.getMyRoles.initiate(
            { token },
            { subscribe: false, forceRefetch: true }
          )
        )
        .unwrap()
      // Rejections are recorded by the matchRejected reducer above.
      // eslint-disable-next-line no-empty
    } catch (error) {
    } finally {
      // Belt and braces: the gate must end up in a terminal state even if the
      // request neither fulfilled nor reported a failure.
      const auth = listenerApi.getState().auth
      if (!auth?.rolesLoaded && !auth?.rolesPending && !auth?.rolesError) {
        listenerApi.dispatch(rolesFetchFailed())
      }
    }
  }
})

startAppListening({
  actionCreator: sessionExpired,
  effect: async (_, listenerApi) => {
    // A burst of 401s must produce one sign-out and one message, not several.
    if (!listenerApi.getState().auth?.token) return

    listenerApi.dispatch(
      addSystemNotification({
        message: i18n.t('auth.session-expired'),
        intent: 'error'
      })
    )
    listenerApi.dispatch(authSlice.actions.logout())
  }
})

startAppListening({
  actionCreator: accessForbidden,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(
      addSystemNotification({
        message: i18n.t('auth.access-forbidden'),
        intent: 'error'
      })
    )
  }
})

startAppListening({
  matcher: authApi.endpoints.login.matchFulfilled,
  effect: async (action, listenerApi) => {
    setToLocalStorage({
      authSource: action.payload.authSource || null
    })
    history.push(ORDERS_PATH)
    listenerApi.dispatch(startInitialization())

    if (action?.payload?.id) {
      logFirebaseEvent('AC_Web_ActivatedAccount', {
        Event_Time: format(new Date(), 'dd/MM/yyyy - HH:mm:ss'),
        Store_ID: action.payload.id
      })

      setClarityPostLogin({
        storeId: action.payload.id,
        storeType: action.payload.type,
        currencyCode: action.payload.currencyCode
      })
    }
  }
})

export const { logout, rolesFetchFailed } = authSlice.actions

export const authInitialState = initialState
export default authSlice.reducer
